import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { SEO, EASE } from "@/components/motion";
import { SummaryList } from "@/components/booking/StepReview";
import { gbp, formatDate } from "@/lib/booking";

const btn = "rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors";

function Shell({ testId, children }) {
  return <main data-testid={testId} className="mx-auto max-w-2xl px-5 sm:px-8 pt-40 pb-32">{children}</main>;
}

function Actions() {
  return (
    <div className="mt-10 flex flex-wrap gap-4">
      <Link to="/" data-testid="confirmation-home-button" className={`${btn} bg-blue text-canvas hover:bg-blue-hover`}>Back to Home</Link>
      <a href="https://wa.me/447939365873" target="_blank" rel="noopener noreferrer" data-testid="confirmation-whatsapp-button"
        className={`${btn} border border-subtle text-ink hover:border-blue hover:text-blue`}>WhatsApp us</a>
    </div>
  );
}

function Paid({ b }) {
  const rows = [
    ["Reference", b.booking_ref],
    ["Service", b.service_name],
    ["Property", b.property_label],
    ...(b.frequency_label ? [["Frequency", b.frequency_label]] : []),
    ["Extras", b.extras?.length ? b.extras.map((e) => e.name).join(", ") : "None"],
    ["Date", formatDate(b.date)],
    ["Preferred time", `${b.time_slot_label} (arrival window)`],
    ["Address", `${b.address}, ${b.postcode}`],
    ...(b.promo_code ? [
      ["Cleaning price", gbp(b.original_cleaning_price)],
      ["Promo code", b.promo_code],
      ["Discount", `-${gbp(b.discount_amount)}`],
      ["Discounted cleaning", gbp(b.final_cleaning_price)],
    ] : []),
    ["Amount paid", gbp(b.total_price)],
  ];
  return (
    <>
      <motion.span initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-blue text-canvas"><Check size={28} /></motion.span>
      <h1 className="mt-8 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Booking confirmed.</h1>
      <p className="mt-4 text-body leading-relaxed">
        Thank you, {b.name?.split(" ")[0]}. Your payment has been received and your reference is{" "}
        <strong data-testid="booking-ref" className="text-blue">{b.booking_ref}</strong>. A confirmation email is on its way.
      </p>
      <div className="mt-8"><SummaryList rows={rows} testId="confirmation-summary" /></div>
      <p className="mt-5 text-sm leading-relaxed text-faint">
        Your preferred time is an arrival window. We'll be in touch to confirm your exact arrival time.
      </p>
      <Actions />
    </>
  );
}

export function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/session/${sessionId}`);
        setBooking(data);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "cancelled", "failed"].includes(data.payment_status)) return setState("failed");
      } catch (e) {
        if (e.response?.status === 404) return setState("missing");
      }
      tries += 1;
      if (tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else setState("missing");
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testId="booking-confirmation">
      <SEO title="Booking Confirmation | HOMELARA" description="Your HOMELARA booking confirmation." />
      {state === "paid" && <Paid b={booking} />}
      {state === "checking" && <p data-testid="booking-checking" className="font-mono text-xs uppercase tracking-[0.2em] text-faint">Confirming your payment with Stripe...</p>}
      {state !== "paid" && state !== "checking" && (
        <div data-testid="booking-not-paid">
          <h1 className="font-serif text-4xl tracking-tight text-ink">
            {state === "timeout" ? "We're still confirming your payment." : "Payment not completed."}
          </h1>
          <p className="mt-4 text-body leading-relaxed">
            {state === "timeout"
              ? "This can take a moment. You'll receive a confirmation email as soon as Stripe confirms your payment."
              : "Your booking hasn't been paid, so it isn't confirmed. You can use the payment link in your HOMELARA email to try again."}
          </p>
          <div className="mt-8"><Link to="/contact" data-testid="booking-retry-button" className={`${btn} bg-blue text-canvas hover:bg-blue-hover`}>Contact us</Link></div>
        </div>
      )}
    </Shell>
  );
}

export function RequestReceived({ bookingRef, email }) {
  return (
    <Shell testId="booking-request-received">
      <SEO title="Booking Request Received | HOMELARA" description="Your HOMELARA booking request has been received." />
      <motion.span initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-blue text-canvas"><Check size={28} /></motion.span>
      <h1 className="mt-8 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Thank you for your booking request!</h1>
      <p className="mt-5 text-body leading-relaxed" data-testid="booking-request-message">
        Your preferred date and time are subject to availability. We&rsquo;ll review your request and contact you to confirm your booking or suggest an alternative.
      </p>
      <p className="mt-4 font-medium text-ink leading-relaxed">Your booking is not confirmed and no payment is required at this stage.</p>
      <p className="mt-6 text-sm text-faint">
        Your reference is <strong data-testid="booking-request-ref" className="text-blue">{bookingRef}</strong>. We&rsquo;ve sent a copy of your request to {email}.
      </p>
      <Actions />
    </Shell>
  );
}

function useTokenBooking() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const token = params.get("t") || "";
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/bookings/public/${encodeURIComponent(ref)}`, { params: { t: token } })
      .then(({ data }) => setB(data)).catch((e) => setError(apiError(e)));
  }, [ref, token]);
  return { ref, token, b, setB, error, setError, params };
}

const summaryRows = (b) => [
  ["Reference", b.booking_ref],
  ["Service", b.service_name],
  ["Property", b.property_label],
  ...(b.frequency_label ? [["Frequency", b.frequency_label]] : []),
  ["Extras", b.extras?.length ? b.extras.map((e) => `${e.name} (${gbp(e.price)})`).join(", ") : "None"],
  ["Date", formatDate(b.date)],
  ["Time", `${b.time_slot_label} (arrival window)`],
  ["Address", `${b.address}, ${b.postcode}`],
  ...(b.promo_code ? [["Promo code", `${b.promo_code} (-${gbp(b.discount_amount)})`]] : []),
  ["Total", gbp(b.total_price)],
];

export function BookingPay() {
  const { ref, token, b, error, setError, params } = useTokenBooking();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/bookings/pay", { ref, token });
      if (data.paid) return window.location.reload();
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(apiError(e));
      setBusy(false);
    }
  };
  const payable = b && ["awaiting_payment", "payment_failed_expired"].includes(b.status) && b.payment_status !== "paid";
  return (
    <Shell testId="booking-pay-page">
      <SEO title="Pay for your booking | HOMELARA" description="Secure payment for your approved HOMELARA booking." />
      {!b && <p className="font-mono text-xs uppercase tracking-[0.2em] text-faint" data-testid="booking-pay-loading">{error || "Loading your booking..."}</p>}
      {b && (
        <>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">
            {b.payment_status === "paid" ? "Your booking is confirmed." : payable ? "Your booking is approved." : "Payment isn't available."}
          </h1>
          <p className="mt-4 text-body leading-relaxed" data-testid="booking-pay-message">
            {b.payment_status === "paid"
              ? "Thank you - your payment has been received. Check your email for your confirmation."
              : payable
                ? "Please complete your payment to confirm your booking. Your booking becomes fully confirmed only once payment has been successfully completed."
                : `This booking is currently: ${b.status_label}. Please contact HOMELARA if you have any questions.`}
          </p>
          {params.get("cancelled") && payable && (
            <p className="mt-4 rounded-2xl border border-sage/40 bg-sage-light/50 p-4 text-sm text-body" data-testid="booking-pay-cancelled-note">
              Payment wasn&rsquo;t completed and nothing has been charged. You can try again below whenever you&rsquo;re ready.
            </p>
          )}
          <div className="mt-8"><SummaryList rows={summaryRows(b)} testId="booking-pay-summary" /></div>
          {error && <p className="mt-5 text-sm text-red-700" data-testid="booking-pay-error">{error}</p>}
          {payable && (
            <button onClick={go} disabled={busy} data-testid="booking-pay-button" className={`${btn} mt-8 bg-blue text-canvas hover:bg-blue-hover disabled:opacity-60`}>
              {busy ? "Opening secure payment..." : `Pay ${gbp(b.total_price)} securely`}
            </button>
          )}
          <Actions />
        </>
      )}
    </Shell>
  );
}

export function BookingRespond() {
  const { ref, token, b, setB, error, setError, params } = useTokenBooking();
  const [busy, setBusy] = useState(false);
  const answer = async (action) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/bookings/respond", { ref, token, action });
      setB(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const p = b?.proposal;
  const waiting = b?.status === "awaiting_customer_response" && p && !p.response;
  const preferred = params.get("action");
  return (
    <Shell testId="booking-respond-page">
      <SEO title="Alternative booking time | HOMELARA" description="Respond to an alternative time proposed by HOMELARA." />
      {!b && <p className="font-mono text-xs uppercase tracking-[0.2em] text-faint">{error || "Loading your booking..."}</p>}
      {b && waiting && (
        <>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">An alternative time for your booking.</h1>
          <p className="mt-4 text-body leading-relaxed">
            You requested <strong>{formatDate(b.requested_date)} &middot; {b.requested_time_slot_label}</strong>. We can offer{" "}
            <strong data-testid="respond-proposed" className="text-blue">{formatDate(p.date)} &middot; {p.time_slot_label}</strong>.
          </p>
          {p.message && <p className="mt-4 rounded-2xl border border-subtle bg-warmcard p-5 text-sm italic text-body">&ldquo;{p.message}&rdquo;</p>}
          <p className="mt-4 text-sm text-faint">Accepting doesn&rsquo;t confirm your booking yet. We&rsquo;ll review your response and then send you a secure payment link.</p>
          {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
          <div className="mt-8 flex flex-wrap gap-4">
            <button disabled={busy} onClick={() => answer("accept")} data-testid="respond-accept-button"
              className={`${btn} ${preferred === "decline" ? "border border-blue text-blue" : "bg-blue text-canvas hover:bg-blue-hover"}`}>Accept this time</button>
            <button disabled={busy} onClick={() => answer("decline")} data-testid="respond-decline-button"
              className={`${btn} ${preferred === "decline" ? "bg-blue text-canvas hover:bg-blue-hover" : "border border-subtle text-ink hover:border-blue hover:text-blue"}`}>Decline</button>
          </div>
        </>
      )}
      {b && !waiting && (
        <div data-testid="respond-done">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">
            {p?.response === "accepted" ? "Thank you - we've received your response." : p?.response === "declined" ? "Thank you for letting us know." : "This proposal is no longer open."}
          </h1>
          <p className="mt-4 text-body leading-relaxed" data-testid="respond-done-message">
            {p?.response === "accepted"
              ? "We'll complete our final checks and email you a secure payment link. Your booking is not confirmed until payment is complete."
              : p?.response === "declined"
                ? "We'll be in touch to find another time that suits you."
                : `Your booking is currently: ${b.status_label}.`}
          </p>
          <Actions />
        </div>
      )}
    </Shell>
  );
}

export function BookingCancelled() {
  return (
    <Shell testId="booking-cancelled">
      <SEO title="Payment not completed | HOMELARA" description="Your HOMELARA payment was not completed." />
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">Payment not completed.</h1>
      <p className="mt-4 text-body leading-relaxed" data-testid="booking-cancelled-message">
        No payment was taken. You can use the secure payment link in your HOMELARA email to try again whenever you&rsquo;re ready.
      </p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/contact" data-testid="booking-contact-button" className={`${btn} border border-subtle text-ink hover:border-blue hover:text-blue`}>Contact us</Link>
      </div>
    </Shell>
  );
}
